import parseLoggedInUser from 'utils/auth/parseLoggedInUser';
import LogRocket from 'logrocket';
import types from './types';

const {
  AUTH_CALLBACK, AUTH_INIT,
  LOGIN, LOGIN_SUCCESS, LOGIN_FAIL,
  REFRESH, REFRESH_SUCCESS, REFRESH_FAIL,
  RESEND, RESEND_SUCCESS, RESEND_FAIL, RESEND_CLEAR,
  VERIFY, VERIFY_SUCCESS, VERIFY_FAIL,
  PASSKEY_LOGIN_SUCCESS,
  PASSKEY_REGISTERED,
  SESSION_VERIFIED,
  PASSKEY_REVOKE_SUCCESS,
  RECOVERY_STATUS_SUCCESS,
  RECOVERY_GENERATE_SUCCESS,
  LOAD_OWNERS, LOAD_OWNERS_SUCCESS, LOAD_OWNERS_FAIL,
  SEND_REQUEST, SEND_REQUEST_SUCCESS, SEND_REQUEST_FAIL,
  VALIDATE_MOBILE, VALIDATE_MOBILE_SUCCESS, VALIDATE_MOBILE_FAIL,
  SELECT_2FA_DELIVERY_METHOD,
  SET_USER, CLEAN_UP,
} = types;

const initialState = {
  loading: false,
  loaded: false,
  loggingIn: false,
  loggedIn: false,
  verifying: false,
  verified: false,
  resending: false,
  resent: false,
  twoFaUsername: null,
  twoFactorRequestId: null,
  twoFactorRequestExpiry: null,
  twoFactorRequestGenerated: false,
  twoFactorDeliveryMethod: null,
  trustedDevice: null,
  validatingMobile: false,
  validatedMobile: false,
  phone: null,
  mobileValidationError: null,
  token: null,
  user: null,
  admin: null,
  owners: [],
  provider: null,
  // OWN-224 step-up: session-only, never persisted server-side. False for the
  // whole session until a step-up verification succeeds (see SESSION_VERIFIED
  // below) - reset to false on every real login (see LOGIN_SUCCESS/
  // PASSKEY_LOGIN_SUCCESS/VERIFY_SUCCESS/AUTH_CALLBACK), but left untouched by
  // a silent REFRESH_SUCCESS.
  sessionVerified: false,
  error: {},
};

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case LOGIN:
      return {
        ...state,
        loggingIn: true,
        loggedIn: false,
        user: null,
        auth2faOptions: null,
        admin: null,
        owners: [],
        error: {},
      };
    case REFRESH_SUCCESS:
    case LOGIN_SUCCESS:
    {
      const { result } = action;
      const {
        id, twoFactorRequestId, twoFactorRequestExpiry, twoFactorRequestGenerated, ttl, used2FA, usedPasskey,
        hasPasskey, passkeyRegistrationRequired, user, username, auth2faOptions,
      } = result;
      const user2Fa = twoFactorRequestId !== undefined;

      if (user2Fa || auth2faOptions) {
        return {
          ...state,
          twoFaUsername: username,
          twoFactorRequestId,
          twoFactorRequestExpiry,
          twoFactorRequestGenerated,
          auth2faOptions,
          loggingIn: false,
          loggedIn: false,
          // Start the timer
          resent: true,
        };
      }
      const userState = parseLoggedInUser(user);
      if (user?.id?.toString()) {
        LogRocket.identify(user.id.toString());
      }
      return {
        ...state,
        token: id,
        ttl,
        loggingIn: false,
        loggedIn: true,
        used2FA,
        usedPasskey,
        hasPasskey,
        passkeyRegistrationRequired,
        // OWN-224 step-up: only a real login resets this to false - REFRESH_SUCCESS
        // is a silent background token renewal (see auth middleware's idle timer),
        // not a new session, and must not undo an already-verified session.
        ...(action.type === LOGIN_SUCCESS ? { sessionVerified: false } : {}),
        ...userState,
      };
    }
    case LOGIN_FAIL:
    case REFRESH_FAIL:
      return {
        ...state,
        loggingIn: false,
        loggedIn: false,
        token: null,
        auth2faOptions: null,
        user: null,
        admin: null,
        error: action.error,
      };
    case REFRESH:
      return {
        ...state,
      };
    case RESEND:
      return {
        resending: true,
        resent: false,
        ...state,
      };
    case RESEND_SUCCESS:
      return {
        ...state,
        resending: false,
        resent: true,
        twoFactorRequestId: action.result.twoFactorRequestId,
        twoFactorRequestExpiry: action.result.twoFactorRequestExpiry,
        twoFactorRequestGenerated: action.result.twoFactorRequestGenerated,
      };
    case RESEND_FAIL:
      return {
        ...state,
        resending: false,
        resent: false,
        error: action.error,
      };
    case RESEND_CLEAR:
      return {
        ...state,
        resending: false,
        resent: false,
        error: {},
      };
    case VERIFY:
      return {
        ...state,
        verifying: true,
        verified: false,
      };
    // A verified passkey assertion starts a session directly (no separate 2FA
    // step), so it's handled identically to a completed VERIFY_SUCCESS.
    case PASSKEY_LOGIN_SUCCESS:
    case VERIFY_SUCCESS: {
      const { id, trustedDevice, ttl, usedPasskey, hasPasskey, passkeyRegistrationRequired, user } = action.result;
      const userState = parseLoggedInUser(user);
      return {
        ...state,
        token: id,
        ttl,
        verifying: false,
        verified: true,
        // Remove both twoFa fields after it has been verified as it is useless after this.
        auth2faOptions: null,
        twoFactorRequestId: null,
        twoFactorRequestGenerated: false,
        twoFaUsername: null,
        trustedDevice,
        usedPasskey,
        hasPasskey,
        passkeyRegistrationRequired,
        // OWN-224 step-up: both of these complete a login (passkey login, or
        // password + 2FA) - never starts a session already "verified".
        sessionVerified: false,
        ...userState,
      };
    }
    // Local, immediate flip after a successful registration - lets the
    // mandatory-registration hard-block (and the pre-deadline nudge) clear
    // right away without waiting for the next login/refresh cycle.
    case PASSKEY_REGISTERED:
      return {
        ...state,
        hasPasskey: true,
        passkeyRegistrationRequired: false,
      };
    // OWN-224 step-up: local, immediate flip once any of the three step-up
    // verification methods (passkey, password+2FA, recovery code) succeeds on
    // the VerificationRequired page - see grantAndGo there. Stays true for the
    // rest of the session (see initialState/LOGIN_SUCCESS above for the only
    // places it resets to false).
    case SESSION_VERIFIED:
      return {
        ...state,
        sessionVerified: true,
      };
    // Mirrors PASSKEY_REGISTERED for the opposite direction - owner-api
    // recomputes both flags on every revoke, so removing the last passkey
    // re-triggers the FTR-3 nudge/hard-block immediately, not on next login.
    case PASSKEY_REVOKE_SUCCESS:
      return {
        ...state,
        hasPasskey: action.result.hasPasskey,
        passkeyRegistrationRequired: action.result.passkeyRegistrationRequired,
      };
    // Shared between the Security & Login page and App.js's global low-codes
    // banner, so generating a fresh batch there is immediately reflected here
    // without a second recovery/status round-trip.
    case RECOVERY_STATUS_SUCCESS:
      return {
        ...state,
        recoveryCodesRemaining: action.result?.remaining ?? null,
      };
    case RECOVERY_GENERATE_SUCCESS:
      return {
        ...state,
        recoveryCodesRemaining: action.result?.codes?.length ?? null,
      };
    case VERIFY_FAIL: {
      return {
        ...state,
        verifying: false,
        verified: false,
        error: action.error,
      };
    }
    case LOAD_OWNERS:
      return {
        ...state,
        loading: true,
        loaded: false,
        owners: [],
        error: {},
      };
    case LOAD_OWNERS_SUCCESS: {
      return {
        ...state,
        loading: false,
        loaded: true,
        owners: action.result,
      };
    }
    case LOAD_OWNERS_FAIL:
      return {
        ...state,
        loading: false,
        loaded: false,
        error: action.error,
      };
    case AUTH_INIT:
      // The full-page redirect to the auth service lives in auth middleware (side effect).
      return state;
    case AUTH_CALLBACK: {
      const { result } = action;
      const {
        id, twoFactorRequestId, twoFactorRequestExpiry, twoFactorRequestGenerated,
        hasPasskey, passkeyRegistrationRequired, user, username, auth2faOptions,
      } = result;
      const user2Fa = twoFactorRequestId !== undefined;

      if (user?.id?.toString()) {
        LogRocket.identify(user.id.toString());
      }

      if (user2Fa || auth2faOptions) {
        return {
          ...state,
          twoFaUsername: username,
          twoFactorRequestId,
          twoFactorRequestExpiry,
          twoFactorRequestGenerated,
          auth2faOptions,
          loggingIn: false,
          loggedIn: false,
          resent: true,
        };
      }

      const userState = parseLoggedInUser(user);
      return {
        ...state,
        token: id,
        loggingIn: false,
        loggedIn: true,
        provider: 'google',
        hasPasskey,
        passkeyRegistrationRequired,
        // OWN-224 step-up: a fresh Google OAuth login is a new session too.
        sessionVerified: false,
        ...userState,
      };
    }
    case SET_USER:
      return {
        ...state,
        user: action.user,
      };
    case SELECT_2FA_DELIVERY_METHOD:
      return {
        ...state,
        twoFactorDeliveryMethod: action.deliveryMethod,
      };
    case SEND_REQUEST:
      return {
        ...state,
        loading: true,
        loaded: false,
        error: {},
      };
    case SEND_REQUEST_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
      };
    case SEND_REQUEST_FAIL:
      return {
        ...state,
        loading: false,
        loaded: false,
        error: action.error,
      };
    case CLEAN_UP:
      return {
        ...state,
        loading: false,
        loaded: false,
        error: {},
      };
    case VALIDATE_MOBILE: {
      return {
        ...state,
        validatingMobile: true,
        validatedMobile: false,
        mobileValidationError: null,
        phone: null,
      };
    }
    case VALIDATE_MOBILE_SUCCESS: {
      return {
        ...state,
        validatingMobile: false,
        validatedMobile: true,
        phone: action.result,
      };
    }
    case VALIDATE_MOBILE_FAIL:
      return {
        ...state,
        validatingMobile: false,
        validatedMobile: false,
        mobileValidationError: !!action.error,
      };
    default:
      return state;
  }
}
